import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Trash2 } from 'lucide-react';
import { supabase } from '../services/supabase';
import { api } from '../services/api';
import { PageContainer } from '../components/layout/PageContainer';
import { Logo } from '../components/common/Logo';
import { LoadingSpinner } from '../components/common/LoadingSpinner';

export function ResetPage() {
  const navigate = useNavigate();
  const [done, setDone] = useState(false);

  useEffect(() => {
    const reset = async () => {
      try {
        // Push first, while there is still a service worker to ask and a token
        // to authenticate with: unregistering the SW would strand
        // `serviceWorker.ready`, and clearing localStorage below takes the
        // Bearer token the server-side unsubscribe needs.
        try {
          if ('serviceWorker' in navigator && 'PushManager' in window) {
            // getRegistration(), not `ready`: with nothing registered `ready`
            // never settles and the reset page would hang on its spinner.
            const reg = await navigator.serviceWorker.getRegistration();
            const subscription = await reg?.pushManager?.getSubscription();
            if (subscription) {
              await api.vision.push
                .unsubscribe({ endpoint: subscription.endpoint })
                .catch(() => undefined);
              await subscription.unsubscribe();
            }
          }
        } catch {
          // No active SW or push not supported — ignore
        }

        // Sign out from Supabase (ignore errors — session may already be invalid)
        await supabase.auth.signOut({ scope: 'local' }).catch(() => {});

        // Clear all localStorage
        localStorage.clear();

        // Clear all sessionStorage
        sessionStorage.clear();

        // Clear cookies for this domain
        document.cookie.split(';').forEach((cookie) => {
          const name = cookie.split('=')[0].trim();
          document.cookie = `${name}=;expires=${new Date(0).toUTCString()};path=/`;
        });

        // Unregister service workers
        if ('serviceWorker' in navigator) {
          const registrations = await navigator.serviceWorker.getRegistrations();
          await Promise.all(registrations.map((reg) => reg.unregister()));
        }

        // Clear Cache Storage (service worker caches)
        if ('caches' in window) {
          const cacheNames = await caches.keys();
          await Promise.all(cacheNames.map((name) => caches.delete(name)));
        }
      } catch (err) {
        console.error('[Reset] Error during cleanup:', err);
      }

      setDone(true);
    };

    reset();
  }, []);

  return (
    <PageContainer className="flex items-center justify-center min-h-screen px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center"
      >
        <Logo size="lg" className="mb-8" />

        {!done && (
          <div className="flex flex-col items-center gap-4">
            <LoadingSpinner size="lg" />
            <p className="text-white/60">Limpando dados do navegador...</p>
          </div>
        )}

        {done && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', duration: 0.5 }}
            className="flex flex-col items-center gap-4"
          >
            <div className="w-20 h-20 rounded-full bg-success-green/20 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-success-green" />
            </div>
            <h2 className="text-xl font-semibold text-white">Dados limpos!</h2>
            <p className="text-white/60 max-w-xs">
              Todos os dados do navegador foram removidos. Você pode fazer login novamente.
            </p>
            <button
              onClick={() => navigate('/login', { replace: true })}
              className="mt-4 flex items-center gap-2 px-6 py-3 bg-accent-blue hover:bg-accent-blue/80 text-white rounded-xl font-medium transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Ir para o login
            </button>
          </motion.div>
        )}
      </motion.div>
    </PageContainer>
  );
}
